/**
 * Merchants — the places money goes.
 *
 * A flat list of names. Rewe, the bakery, Netflix. Like the item catalogue, it
 * is never "set up": every name here got here because it was typed on a real
 * expense, and merchant is required on one (AGENTS.md §3.5b(iii)). This screen
 * is for the other direction — naming a place before you have spent there —
 * and for tidying afterwards.
 *
 * A merchant is WHERE, an item is WHAT. They are different axes, and they
 * collapse to the same word for a subscription (you pay Netflix for Netflix)
 * without that being a problem: the fields answer different questions, and
 * only the item carries a quantity and a price history. That is also why a
 * merchant may be named after a category — "Supermarket" is a perfectly good
 * answer to where the money went, and exactly the wrong answer to what you
 * bought (§3.5b(ii)).
 *
 * THE ICON IS AN EMOJI, one short string on the row. Not an uploaded logo:
 * this app stores no images at all (§3.6), and a logo would want a blob
 * store, size and type limits, RLS on the blobs and a serving route — a
 * subsystem for a decoration. An emoji is text: it exports, renders offline,
 * and costs nothing. Unset falls back to the name's initial, so every row has
 * something in the slot from the moment it exists and the list never looks
 * half-configured.
 *
 * Rename freely; delete only what nothing points at. `entry.merchant_id` is
 * `ON DELETE SET NULL` and `entry` has no merchant name of its own, so
 * deleting one you have paid does not fail — it blanks the place on every past
 * entry. The row says so rather than offering a button that cannot work.
 */
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type { Merchant } from '@et/contracts';
import { api, messageOf, type Identity } from './api.js';
import { dayLabel } from './months.js';

interface Props {
  identity: Identity;
  merchants: Merchant[];
  canEdit: boolean;
  onChanged: (next: Merchant[]) => void;
}

/**
 * A starting set, not a limit — the field below takes anything. Chosen for the
 * places a household actually pays: shops, bills, transport, health, leisure.
 */
const SUGGESTED = [
  '\u{1F6D2}', '\u{1F35E}', '\u{1F9C0}', '\u{1F355}', '\u2615', '\u{1F37A}',
  '\u{1F3E0}', '\u26A1', '\u{1F4A7}', '\u{1F525}', '\u{1F4F6}', '\u{1F4BB}',
  '\u{1F697}', '\u26FD', '\u{1F686}', '\u2708\uFE0F', '\u{1F3E5}', '\u{1F48A}',
  '\u{1F3AC}', '\u{1F3B5}', '\u{1F4DA}', '\u{1F455}', '\u{1F415}', '\u{1F381}',
];

export function Merchants({ identity, merchants, canEdit, onChanged }: Props) {
  const [name, setName] = useState('');
  /** Which row's icon picker is open. One at a time: two overlap and neither wins. */
  const [iconFor, setIconFor] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Which row is being renamed, and what has been typed into it so far. */
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  async function run(key: string, work: () => Promise<{ merchants: Merchant[] }>) {
    setBusyId(key);
    setError(null);
    try {
      onChanged((await work()).merchants);
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setBusyId(null);
    }
  }

  async function add(event: FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setName('');
    await run('new', () => api.createMerchant(identity, { name: trimmed }));
  }

  /** Picking the one already set is a close, not a write. */
  async function setIcon(m: Merchant, icon: string | null) {
    setIconFor(null);
    if ((m.icon ?? null) === icon) return;
    await run(m.id, () => api.updateMerchant(identity, m.id, { icon }));
  }

  /**
   * Blank or unchanged is a cancel, not a write — same gesture and the same
   * escape routes as every other name in this app (§3.5b(vi)).
   */
  async function commitRename(m: Merchant) {
    const next = draft.trim();
    setRenamingId(null);
    if (!next || next === m.name) return;
    await run(m.id, () => api.updateMerchant(identity, m.id, { name: next }));
  }

  return (
    <section className="settings">
      <div className="settings-block">
        <p className="hint">
          Where your money goes. The list fills itself in — every name you type on an expense is
          here for good — so this is for adding one early, fixing a typo, or dropping one you
          created by mistake. A rename carries every past entry with it.
        </p>

        {!canEdit && <p className="hint">Only a household owner or adult can change these.</p>}
        {error && <p className="error">{error}</p>}

        {canEdit && (
          <form className="cat-add-root" onSubmit={(e) => void add(e)}>
            <div className="cat-row cat-add">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Add a merchant — “Rewe”, “The bakery”…"
                aria-label="New merchant name"
                autoComplete="off"
              />
              <button type="submit" className="chip" disabled={!name.trim() || busyId === 'new'}>
                Add
              </button>
            </div>
          </form>
        )}

        {merchants.length === 0 ? (
          <p className="hint">
            No merchants yet. Record an expense and the place you typed appears here, or add one
            above.
          </p>
        ) : (
          <div className="cat-group">
            {merchants.map((m) => (
              <div className="cat-row" key={m.id}>
                <MerchantIcon
                  merchant={m}
                  canEdit={canEdit}
                  open={iconFor === m.id}
                  busy={busyId === m.id}
                  onToggle={() => setIconFor((id) => (id === m.id ? null : m.id))}
                  onPick={(icon) => void setIcon(m, icon)}
                />

                {renamingId === m.id ? (
                  <span className="cat-name">
                    <input
                      className="cat-rename"
                      value={draft}
                      autoFocus
                      disabled={busyId === m.id}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void commitRename(m);
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          setRenamingId(null);
                        }
                      }}
                      onBlur={() => void commitRename(m)}
                      aria-label={`Rename ${m.name}`}
                    />
                  </span>
                ) : canEdit ? (
                  <button
                    type="button"
                    className="cat-name cat-name-edit"
                    onClick={() => {
                      setRenamingId(m.id);
                      setDraft(m.name);
                    }}
                    title="Rename"
                  >
                    {m.name}
                  </button>
                ) : (
                  <span className="cat-name">{m.name}</span>
                )}

                <span className="merchant-meta">
                  {m.timesUsed > 0 && <span>{m.timesUsed}×</span>}
                  {m.lastUsedAt && <span>{dayLabel(m.lastUsedAt)}</span>}
                </span>

                {canEdit &&
                  (m.blockedBy.entries === 0 ? (
                    <button
                      type="button"
                      className="chip chip-ghost cat-archive"
                      disabled={busyId === m.id}
                      onClick={() => void run(m.id, () => api.deleteMerchant(identity, m.id))}
                    >
                      Delete
                    </button>
                  ) : (
                    /* Not a disabled button: saying why is more use than a
                       control that looks broken. */
                    <span className="hint item-kept">rename instead</span>
                  ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * The icon slot.
 *
 * Always occupied: an emoji when one is set, the name's first letter when not.
 * A blank square would read as a thing that failed to load, and an initial is
 * genuinely useful — it is what tells two shops apart while you are scanning.
 *
 * The slot IS the control, the same gesture as the colour dot on a category
 * (AGENTS.md §3.5h): you click the thing you want to change. It is a real
 * button rather than a div, so it is reachable by keyboard.
 */
function MerchantIcon({
  merchant,
  canEdit,
  open,
  busy,
  onToggle,
  onPick,
}: {
  merchant: Merchant;
  canEdit: boolean;
  open: boolean;
  busy: boolean;
  onToggle: () => void;
  onPick: (icon: string | null) => void;
}) {
  const [typed, setTyped] = useState('');
  const anchor = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!anchor.current?.contains(event.target as Node)) onToggle();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onToggle();
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onToggle]);

  const face = merchant.icon ?? initialOf(merchant.name);
  const glyph = (
    <span className={merchant.icon ? 'merchant-glyph' : 'merchant-glyph is-initial'} aria-hidden="true">
      {face}
    </span>
  );

  if (!canEdit) return glyph;

  return (
    <span className="merchant-icon" ref={anchor}>
      <button
        type="button"
        className="merchant-icon-btn"
        onClick={onToggle}
        aria-expanded={open}
        aria-label={`Icon for ${merchant.name}`}
        disabled={busy}
        title="Choose an icon"
      >
        {glyph}
      </button>

      {open && (
        <div className="popover icon-pop" role="dialog" aria-label={`Icon for ${merchant.name}`}>
          <div className="icon-grid">
            {SUGGESTED.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className={merchant.icon === emoji ? 'icon-choice on' : 'icon-choice'}
                onClick={() => onPick(emoji)}
                aria-label={`Use ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>

          <div className="icon-pop-foot">
            {/* Anything at all, not just the two dozen above — the API only
                insists it is not a word. */}
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                const next = typed.trim();
                if (next) onPick(next);
                setTyped('');
              }}
              placeholder="or paste one"
              aria-label="Type or paste an emoji"
              maxLength={12}
            />
            {merchant.icon && (
              <button type="button" className="chip chip-ghost" onClick={() => onPick(null)}>
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </span>
  );
}

/**
 * The first LETTER, by code point — `charAt(0)` splits an emoji or a non-BMP
 * character in half and renders a replacement box, which is worse than no
 * fallback at all.
 */
function initialOf(name: string): string {
  const first = [...name.trim()][0] ?? '?';
  return first.toLocaleUpperCase();
}
